/**
 * 상태 원값 → 표기 종류. 와이어프레임 `stKind()` 1059행을 그대로 옮겼다.
 *
 * 모듈 상태(대문자)와 배치·레시피·시료 상태(소문자)를 한 사전이 받는다.
 * 🔴 여기서 한글을 만들지 않는다. 화면 표기는 `@ctcells/i18n` 의 `*Label()` 이 만든다 —
 *    같은 원값이 도메인마다 다른 말로 나가기 때문이다.
 */
export type StatusKind =
  'run' | 'idle' | 'wait' | 'err' | 'pause' | 'stop' | 'done' | 'manual' | 'recovery'

/*
 * 모듈 상태 32값(계약 28 + 은퇴 4)의 종류는 **정본이 정한다** — `06 §4.2a` 「공통 분류 ↔ 상태 매핑」이
 * `ModuleStatus.state` enum 전건을 6분류(대기 · 작업 중 · 개입 대기 · 이상 · 복구 중 ·
 * 안전정지)에 배정해 두었다. 여기서 분류를 발명하지 않고 그 표를 옮긴다.
 *
 *   대기      → idle      작업 중 → run       개입 대기 → wait
 *   이상      → err       복구 중 → recovery  안전정지  → stop
 *
 * 🔴 **2026-09-26 — 수거 대기가 «주체로» 두 상태가 됐다**(`06 §4.2a` 「수거 대기가 둘인 까닭」 ·
 *    documents `dbc8488` · module-core `0c30263`). 종전(2026-09-21~25)에는 `WAITING_FOR_UNLOAD`
 *    한 낱말이 원심=개입 대기 · 어셈블러=작업 중으로 **모듈마다 갈려서** 원값 하나만 받는 이
 *    함수가 가를 수 없었다. 이제 갈림이 상태 이름에 들어 있어 **모듈 한정이 없다** —
 *    - `WAITING_FOR_TRAY_UNLOAD` = **작업 중**: 6축이 후면에서 핸드오프 트레이를 가져갈 때까지 ·
 *      「사람 할 일 없음」(`06 §4.2a` 작업 중 행 · 표시 각주 「`WAITING_FOR_TRAY_UNLOAD` = 작업 중」)
 *    - `WAITING_FOR_MANUAL_TRAY_UNLOAD` = **개입 대기**: 사람 전면 수거(수거 확약 목록은 이 상태에서만)
 *    - `WAITING_FOR_MANUAL_TRAY_LOAD` = **개입 대기**: 사람 적재(`SM-MOD-06`)
 *    - `TRAY_LOADING` · `TRAY_UNLOADING` = **작업 중**: 트레이가 모듈 경계를 넘는 동안
 * ⚠ **은퇴 이름**(`06 §4.1` 「은퇴 상태 대응」)은 새 이름과 같은 칸이다 — 정본 표가 그렇게 적었다.
 *    `WAITING_FOR_UNLOAD` → 작업 중(`WAITING_FOR_TRAY_UNLOAD` 와 같이) · `WAITING_FOR_MANUAL_UNLOAD` ·
 *    `WAITING_FOR_MANUAL_LOAD` → 개입 대기 · `OUTPUT` → 작업 중.
 *    🔴 `WAITING_FOR_UNLOAD` 를 `wait` 로 되돌리지 마라 — 정본이 작업 중으로 옮겼다. 사람을 부르는
 *    자리는 이제 `WAITING_FOR_MANUAL_TRAY_UNLOAD` 가 따로 갖는다.
 */
const KIND: Record<string, StatusKind> = {
  // ── ModuleSnapshot.state — 계약 enum 28값 + 은퇴 4값 (`06 §4.2`) ───────────
  // ⚠ 2026-09-29 계약 재생성: 은퇴 4값(`OUTPUT` · `WAITING_FOR_UNLOAD` · `WAITING_FOR_MANUAL_UNLOAD` ·
  //   `WAITING_FOR_MANUAL_LOAD`)이 서버 enum 에서 빠졌다. **지우지 않는다** — 옛 이벤트 기록(이력 · 로그)을
  //   읽을 때 여전히 온다. 아래 해당 줄마다 「은퇴 · 옛 기록 호환」을 달았다.
  // 대기
  IDLE: 'idle',
  // 작업 중
  // 🔴 코어 자동 취소로 멈춘 자리 — 이어 보내기를 기다린다 · 사람 할 일 없음(`06 §4.2a` 작업 중 행 · `SM-MOD-23` ⓑ)
  INTERRUPTED: 'run',
  TRAY_LOADING: 'run',
  TRAY_UNLOADING: 'run',
  LOADING: 'run',
  RUNNING: 'run',
  UNLOADING: 'run',
  TC_LOAD: 'run',
  UV: 'run',
  TC_REMOVE: 'run',
  ROTATE_DRAIN: 'run',
  OUTPUT: 'run', // 은퇴 · 옛 기록 호환
  TIP_CHANGE: 'run',
  PIPETTING: 'run',
  DISPENSING: 'run',
  MOVING: 'run',
  INCUBATING: 'run',
  SHAKING: 'run',
  // 🔴 「스텝 간 대기」다 — 정본이 **작업 중**으로 못박았다. 사람을 부르는 자리가 아니다
  WAITING: 'run',
  COMPLETE: 'run',
  HOMING: 'run',
  HANDOFF: 'run',
  // 🔴 로봇 회수 대기 — 사람 할 일이 없다(`06 §4.2a` 작업 중 행). 은퇴 이름도 같은 칸
  WAITING_FOR_TRAY_UNLOAD: 'run',
  WAITING_FOR_UNLOAD: 'run', // 은퇴 · 옛 기록 호환
  // 개입 대기 — 사람 적재 · 사람 수거(은퇴 이름 둘 포함 · `06 §4.2a` 개입 대기 행)
  // 🔴 사람이 연 취소로 그 자리에 멈춤 — 사람 정리 뒤 원점 복귀(`SM-MOD-23` ⓐ). 주의(▲)다
  ABORTED: 'wait',
  WAITING_FOR_MANUAL_TRAY_LOAD: 'wait',
  WAITING_FOR_MANUAL_TRAY_UNLOAD: 'wait',
  WAITING_FOR_MANUAL_LOAD: 'wait', // 은퇴 · 옛 기록 호환
  WAITING_FOR_MANUAL_UNLOAD: 'wait', // 은퇴 · 옛 기록 호환
  // 이상 · 복구 중 · 안전정지
  ERROR: 'err',
  RECOVERY: 'recovery',
  SAFE_STOP: 'stop',

  // ── 서버가 내지 않는 화면 별칭 (사용자 배지 · 조작 모드가 빌려 쓴다) ──────
  // 🔴 `READY` 를 되살리지 마라(2026-09-20 제거) — 와이어프레임 내부 키였고 정본 30값에 없다.
  //    원값은 `WAITING_FOR_MANUAL_TRAY_LOAD`(은퇴 `WAITING_FOR_MANUAL_LOAD` · 위 「개입 대기」)다.
  //    사전(`ko.ts`)에서도 함께 걷었다.
  PAUSED: 'pause',
  STOPPED: 'stop',
  // 수동 조작 — 작업 상태가 아니라 조작 모드다(§5-2)
  MANUAL: 'manual',

  // batch.status
  running: 'run',
  created: 'wait',
  paused: 'pause',
  completed: 'done',
  aborted: 'err',
  error: 'err',
  // recipe.status
  draft: 'wait',
  committed: 'done',
  archived: 'idle',
  // sample.status
  registered: 'idle',
  centrifuged: 'done',
  assembled: 'done',
  staining: 'run',
  in_process: 'run',
  discarded: 'err',
  // alarm severity
  CRITICAL: 'err',
  WARN: 'wait',
  INFO: 'idle',
  DEBUG: 'idle',
  // 검사 판정
  pass: 'done',
  fail: 'err',
  inconclusive: 'wait',
}

/**
 * 모르는 원값은 'idle' 로 떨어진다 — **모양만** 그렇고 글자는 그대로 나간다.
 * 🔴 배지 «글자»는 `enumLabel()` 이 원값으로 채운다. 여기서 모르는 값을 만나도
 *    배지가 비지 않는다 — 비면 「상태 없음」으로 읽힌다(2026-09-20 실물 사고).
 */
export function statusKind(raw: string): StatusKind {
  return KIND[raw] ?? 'idle'
}

/**
 * 종류 → 심각도. 콘솔 스킨의 «기호를 둘지» 를 가른다 — 정본 `06 §4.2a` 6분류를 시안의 알약 넷에 접었다.
 *   ok   = 작업 중                       → 글자만(강조 면이 말한다)
 *   done = 끝난 것                       → 글자만
 *   idle = 대기 · 안전정지 · 수동 조작   → 글자만(시안 `ST_PILL` stop → `pill--idle`)
 *   warn = 개입 대기 · 일시정지 · 복구 중 → ▲ (시안 `JOB_ST` pause · recover → `pill--warn`)
 *   err  = 이상 · 폐기 · 실패            → 팔각형 ! (시안 `pill--err`)
 * ⚠ 복구 중이 ▲ 를 받아도 「복구 중 ≠ 고장」은 산다 — 고장은 팔각형이고, 점선 테와 글자가 마저 가른다.
 * 스킨 없는 앱에서는 이 값이 겉모습을 바꾸지 않는다(`data-severity` 속성만 붙는다).
 */
export type StatusSeverity = 'ok' | 'warn' | 'err' | 'idle' | 'done'

export const STATUS_SEVERITY: Record<StatusKind, StatusSeverity> = {
  run: 'ok',
  idle: 'idle',
  wait: 'warn',
  err: 'err',
  pause: 'warn',
  stop: 'idle',
  done: 'done',
  manual: 'idle',
  recovery: 'warn',
}

/**
 * 원값 하나만 종류와 다른 심각도를 받는 자리 — **작업 「중단」(`aborted`)은 주의(▲)다**(eric 2026-10-29 「중단된 거는 저렇게 주황색 계열로」 ·
 * 디자인 시안 작업 이력 `.pill--warn`). 종류(`err`)는 그대로 둔다 — 흑백 앱(스킨 없음)은 종전 위험 채움 · ▲ 글리프 그대로다.
 * 콘솔 스킨에서만 보이는 표지(`.a-stb__mark`)가 팔각형 ! 대신 ▲ 가 되고, 면 · 글자는 `--status-abort-*`(theme.css)가 호박으로 쥔다.
 * 🔴 «오류»(`error`)는 그대로 위험이다 — 멈춘 까닭이 고장인 것과 사람이 끝낸 것은 다르다.
 */
const SEVERITY_BY_STATUS: Readonly<Record<string, StatusSeverity>> = {
  aborted: 'warn',
  // 시료 「폐기」도 같은 호박 ▲ 다(eric 2026-10-29 「폐기는... 주황색 아까.. 처럼」)
  discarded: 'warn',
}

/** 원값 → 심각도. 위 덮기 표에 없으면 종류의 심각도(`STATUS_SEVERITY`) */
export function statusSeverity(raw: string): StatusSeverity {
  return SEVERITY_BY_STATUS[raw] ?? STATUS_SEVERITY[statusKind(raw)]
}
